import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { EntitySelect } from '@/components/EntitySelect';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { CURRENCIES, type Currency } from '@/features/crm/deals/constants';
import { getService } from '@/features/finance/services/api';
import { resolveServiceLabel, searchServices } from '@/features/finance/services/select';
import { toDecimalHours } from '@/lib/hours';
import { useAddInvoiceLine } from '../queries';
import type { AddInvoiceLineInput } from '../types';

const schema = z
  .object({
    service: z.string(),
    description: z.string().trim().min(1, 'Description is required'),
    /** How the line is billed — time (hours+minutes) or a plain quantity. */
    mode: z.enum(['time', 'quantity']),
    hours: z.string(),
    minutes: z.string(),
    quantity: z.string(),
    /** Free-text unit label for quantity lines (e.g. "month", "license"). */
    unit: z.string(),
    unit_rate: z.string().refine((v) => v !== '' && Number(v) >= 0, 'Enter a rate'),
    currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
    fx_rate_to_base: z.string().refine((v) => Number(v) > 0, 'Must be > 0'),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'time') {
      if ((Number(v.hours) || 0) + (Number(v.minutes) || 0) / 60 <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a time greater than zero',
          path: ['hours'],
        });
      }
    } else if (!(Number(v.quantity) > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a quantity greater than zero',
        path: ['quantity'],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = [
  'service',
  'description',
  'mode',
  'hours',
  'minutes',
  'quantity',
  'unit',
  'unit_rate',
  'currency',
  'fx_rate_to_base',
] as const;

interface AddInvoiceLineFormProps {
  invoiceId: number;
  defaultCurrency: Currency;
}

export function AddInvoiceLineForm({ invoiceId, defaultCurrency }: AddInvoiceLineFormProps) {
  const addLine = useAddInvoiceLine(invoiceId);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      service: '',
      description: '',
      mode: 'time',
      hours: '1',
      minutes: '0',
      quantity: '1',
      unit: '',
      unit_rate: '',
      currency: defaultCurrency,
      fx_rate_to_base: '1',
    },
  });

  const mode = useWatch({ control: form.control, name: 'mode' });

  // Picking a catalog service prefills name + USD rate (still editable, and
  // optional — leave it empty for an ad-hoc line).
  async function onServiceChange(value: string) {
    form.setValue('service', value);
    if (!value) return;
    try {
      const svc = await getService(Number(value));
      form.setValue('description', svc.name, { shouldValidate: true });
      form.setValue('unit_rate', svc.base_rate, { shouldValidate: true });
      form.setValue('currency', 'USD');
      form.setValue('fx_rate_to_base', '1');
    } catch {
      /* leave fields as-is if the lookup fails */
    }
  }

  async function handleSubmit(values: FormValues) {
    const isTime = values.mode === 'time';
    const input: AddInvoiceLineInput = {
      service: values.service ? Number(values.service) : null,
      description: values.description,
      // Time → decimal hours tagged "hour" (renders "Hh Mm"); Quantity → the raw
      // number with the free-text unit (e.g. "month"), rendered as a plain qty.
      quantity: isTime ? toDecimalHours(values.hours, values.minutes) : values.quantity.trim(),
      unit: isTime ? 'hour' : values.unit.trim(),
      unit_rate: values.unit_rate,
      currency: values.currency,
      fx_rate_to_base: values.fx_rate_to_base,
    };
    try {
      await addLine.mutateAsync(input);
      form.reset({
        ...form.getValues(),
        service: '',
        description: '',
        hours: '1',
        minutes: '0',
        quantity: '1',
        unit: '',
        unit_rate: '',
      });
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          // The server field is `quantity`; surface its error on the active input.
          const key =
            field === 'quantity'
              ? isTime
                ? 'hours'
                : 'quantity'
              : FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Service (optional)</FormLabel>
              <FormControl>
                <EntitySelect
                  queryKey="invoice-line-service-picker"
                  value={field.value}
                  onChange={onServiceChange}
                  search={searchServices}
                  resolveLabel={resolveServiceLabel}
                  placeholder="Pick from catalog, or type a line below"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Consulting — 10h" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="mode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bill by</FormLabel>
              <div className="flex w-fit rounded-md border p-0.5">
                {(
                  [
                    { value: 'time', label: 'Time' },
                    { value: 'quantity', label: 'Quantity' },
                  ] as const
                ).map((opt) => (
                  <Button
                    key={opt.value}
                    type="button"
                    size="sm"
                    variant={field.value === opt.value ? 'default' : 'ghost'}
                    className="h-7"
                    onClick={() => field.onChange(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </FormItem>
          )}
        />
        <div className="grid gap-4 sm:grid-cols-5">
          {mode === 'time' ? (
            <>
              <FormField
                control={form.control}
                name="hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hours</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minutes</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} max={59} step="1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : (
            <>
              <FormField
                control={form.control}
                name="quantity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. month" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <FormField
            control={form.control}
            name="unit_rate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit rate</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.01" placeholder="0.00" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="fx_rate_to_base"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FX → USD</FormLabel>
                <FormControl>
                  <Input type="number" min={0} step="0.00000001" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}

        <Button type="submit" size="sm" disabled={addLine.isPending}>
          <Plus /> {addLine.isPending ? 'Adding…' : 'Add line'}
        </Button>
      </form>
    </Form>
  );
}
